import uniqBy from 'lodash.uniqby';
import React, { useState, useEffect, FC } from 'react';
import cn from 'classnames';
import { connect } from 'react-redux';
import useDeviceType from '@hooks/useDeviceType';
import useSearcher from '@hooks/useSearcher';
import Categories from './private/Categories';
import Services from './private/Services';
import ServicesList from './private/ServicesList';
import Subcategories from './private/Subcategories';
import ServiceSearch from './private/Search';
import {
  serviceCatalogSelector,
  ServiceCatalogSelectorProps,
} from './structuredSelector';

import st from './ServicesCatalog.module.css';

const shouldRenderCategories = (category, subcategory, desktop) =>
  Boolean(desktop || (!category && !subcategory));

const shouldRenderSubcategories = (category, subcategory, desktop) =>
  Boolean(category && (desktop || !subcategory));

const shouldRenderServices = (category, subcategory, desktop) =>
  Boolean(subcategory && (desktop || category));

const getServicesList = (catalog) => {
  const servicesList = catalog.reduce(
    (categories, rootCategory) => [
      ...categories,
      ...rootCategory.categories.reduce(
        (services, category) => [...services, ...category.services],
        []
      ),
    ],
    []
  );

  return uniqBy(servicesList, (service) => service.id);
};

interface ServicesCatalogProps extends ServiceCatalogSelectorProps {
  isSearchSeparated?: boolean;
  onClose: any;
}

const ServicesCatalog: FC<ServicesCatalogProps> = ({
  catalog,
  office,
  isSearchSeparated = true,
  onClose,
}) => {
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const {
    search,
    onSearch,
    foundItems: foundServices,
  } = useSearcher(getServicesList(catalog));
  const { desktop } = useDeviceType();
  const onCategoryReset = () => setCategory(null);
  const onSubcategoryReset = () => setSubcategory(null);
  const onCategorySelect = (selectCategory) => {
    setCategory(selectCategory);
    if (desktop) {
      setSubcategory(selectCategory?.categories[0]);
    }
  };

  useEffect(() => {
    if (desktop) {
      setCategory(catalog[0]);
      setSubcategory(catalog[0]?.categories[0]);
    }
  }, [catalog, desktop]);

  return (
    <div data-testid="modalWrapper" className={st.wrapper}>
      <ServiceSearch
        isSeparated={isSearchSeparated}
        value={search}
        onChange={onSearch}
      />
      {search.trim() ? (
        <div
          className={cn(st.searchResults, {
            [st.separatedSearch]: isSearchSeparated,
          })}
        >
          <ServicesList
            // @ts-ignore
            services={foundServices}
            office={office}
            onClose={onClose}
          />
        </div>
      ) : (
        <div
          className={cn(st.catalog, {
            [st.separatedSearch]: isSearchSeparated,
          })}
        >
          {shouldRenderCategories(category, subcategory, desktop) && (
            <Categories
              categories={catalog}
              category={category}
              onCategorySelect={onCategorySelect}
            />
          )}
          {shouldRenderSubcategories(category, subcategory, desktop) && (
            <Subcategories
              category={category}
              subcategory={subcategory}
              subcategories={
                catalog.find((c) => c.id === category.id)?.categories
              }
              // @ts-ignore
              onSubcategorySelect={setSubcategory}
              onCategoryReset={onCategoryReset}
            />
          )}
          {shouldRenderServices(category, subcategory, desktop) && (
            <Services
              services={subcategory.services}
              subcategory={subcategory}
              office={office}
              onSubcategoryReset={onSubcategoryReset}
              onClose={onClose}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default connect(serviceCatalogSelector, null)(ServicesCatalog);
